import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Attach X-User-Id header on every request so backend can enforce role-based guards
axios.interceptors.request.use((config) => {
  try {
    const stored = localStorage.getItem('user');
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed?.id) {
        config.headers = config.headers || {};
        config.headers['X-User-Id'] = parsed.id;
      }
    }
  } catch (e) {
    // Ignore localStorage / parse errors
  }
  return config;
});

// Auth
export const loginApi = (email, password) => 
  axios.post(`${API}/auth/login`, { email, password });

// Users
export const getUsersApi = () => 
  axios.get(`${API}/users`);

export const createUserApi = (userData) => 
  axios.post(`${API}/users`, userData);

export const updateUserApi = (userId, updateData) => 
  axios.put(`${API}/users/${userId}`, updateData);

export const deleteUserApi = (userId) => 
  axios.delete(`${API}/users/${userId}`);

// Brands
export const getBrandsApi = () => 
  axios.get(`${API}/brands`);

export const createBrandApi = (brandData) => 
  axios.post(`${API}/brands`, brandData);

export const updateBrandApi = (brandId, updateData) => 
  axios.put(`${API}/brands/${brandId}`, updateData);

export const uploadBrandLogoApi = (brandId, logo) => 
  axios.post(`${API}/brands/${brandId}/logo`, { logo });

// Slots
export const getSlotsApi = () => 
  axios.get(`${API}/slots`);

export const createSlotApi = (slotData) => 
  axios.post(`${API}/slots`, slotData);

export const updateSlotApi = (slotId, updateData) => 
  axios.put(`${API}/slots/${slotId}`, updateData);

export const deleteSlotApi = (slotId) => 
  axios.delete(`${API}/slots/${slotId}`);

// Cohorts
export const getCohortsApi = (brandId = null) => {
  const url = brandId ? `${API}/cohorts?brand_id=${brandId}` : `${API}/cohorts`;
  return axios.get(url);
};

export const createCohortApi = (cohortData) => 
  axios.post(`${API}/cohorts`, cohortData);

export const updateCohortApi = (cohortId, updateData) => 
  axios.put(`${API}/cohorts/${cohortId}`, updateData);

export const deleteCohortApi = (cohortId) => 
  axios.delete(`${API}/cohorts/${cohortId}`);

export const uploadCohortsCsvApi = (csvContent) => 
  axios.post(`${API}/cohorts/upload-csv`, { csv_content: csvContent });

// Requests
export const getRequestsApi = (userId = null, brandId = null) => {
  let url = `${API}/requests`;
  const params = [];
  if (userId) params.push(`user_id=${userId}`);
  if (brandId) params.push(`brand_id=${brandId}`);
  if (params.length > 0) url += `?${params.join('&')}`;
  return axios.get(url);
};

export const createRequestApi = async (requestData, userId, brandId) => {
  // Handle CSV file if present
  let csvContent = null;
  if (requestData.cohort_csv && requestData.cohort_csv instanceof File) {
    csvContent = await fileToBase64(requestData.cohort_csv);
  }
  
  return axios.post(`${API}/requests`, {
    ...requestData,
    cohort_csv_content: csvContent,
    cohort_csv: undefined, // Remove file object
    user_id: userId,
    brand_id: brandId
  });
};

export const updateRequestApi = (requestId, updateData) => 
  axios.put(`${API}/requests/${requestId}`, updateData);

export const deleteRequestApi = (requestId) =>
  axios.delete(`${API}/requests/${requestId}`);

export const approveRequestApi = (requestId) => 
  axios.post(`${API}/requests/${requestId}/approve`);

export const rejectRequestApi = (requestId, reason = '') => 
  axios.post(`${API}/requests/${requestId}/reject`, { rejection_reason: reason });

// Helper function to convert file to base64
const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

export const getAvailableSlotsApi = (date) => 
  axios.get(`${API}/requests/available-slots?date=${date}`);

export const getCalendarBookingsApi = (startDate, endDate) =>
  axios.get(`${API}/requests/calendar-bookings?start_date=${startDate}&end_date=${endDate}`);

// Campaigns
export const getCampaignsApi = (brandId = null) => {
  const url = brandId ? `${API}/campaigns?brand_id=${brandId}` : `${API}/campaigns`;
  return axios.get(url);
};

export const updateCampaignApi = (campaignId, updateData) => 
  axios.put(`${API}/campaigns/${campaignId}`, updateData);

// Settings
export const getSettingsApi = () => 
  axios.get(`${API}/settings`);

export const updateSettingsApi = (updateData) => 
  axios.put(`${API}/settings`, updateData);
